import FormattedMessage from 'I18n/components/FormattedMessage';
import { events } from 'growth-payments-core/usage-tracker/events';
import { TrackedButton } from 'growth-payments-core/usage-tracker/components/Button';
import { useCallback, useEffect } from 'react';
import { Metrics } from '../../utils/metrics';
import { useQueryStringState } from 'growth-payments-core/query-string/useQueryStringState';
import { EnrollmentFlowType } from '../../types';
import { markSetUpPaymentsComplete } from '../../utils/tasks';
import { navigateToWithQueryParams } from 'growth-payments-core/urls/utils';
import { getCommerceHomeUrl } from 'growth-payments-core/urls/urls';
import { getFullUrl } from 'hubspot-url-utils';
import CelebrationModalV2 from './CelebrationModalV2';
import { addSuccessAlert } from 'growth-payments-core/alerts';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
// @ts-expect-error untyped-module
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import { usePaymentsEnrollmentUtilitiesContext } from '../../context/PaymentsEnrollmentUtilitiesContext';
import TrackRender from 'growth-payments-core/usage-tracker/TrackRender';
import { track } from 'growth-payments-core/usage-tracker/track';
import { useHasSeenUseCasesModal } from '../../hooks/useHasSeenUseCasesModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const TEST_IDS = {
  KYC_SUCCESS_MODAL_VERIFY: 'kyc-success-modal-verify'
};
const KB_UNDERWRITING = 'https://knowledge.hubspot.com/payment-processing/hubspot-payments-how-to-prepare-for-underwriting';
const ApplicationSuccessModal = ({
  onClose,
  shouldReloadOnEnrollmentComplete = true
}) => {
  const {
    currentEnrollmentFlow,
    businessTypeDetails,
    location,
    returnUrl,
    sourceApp
  } = usePaymentsEnrollmentUtilitiesContext();
  const {
    businessType,
    businessStructure
  } = businessTypeDetails;
  const [, setCurrentEnrollmentFlow] = useQueryStringState('paymentsEnrollment', EnrollmentFlowType.processorPicker);
  const {
    hasSeenUseCasesModal
  } = useHasSeenUseCasesModal();
  const handleClick = useCallback(() => {
    setCurrentEnrollmentFlow(undefined);
    if (shouldReloadOnEnrollmentComplete) {
      const fullUrl = getFullUrl('app');
      const targetUrl = returnUrl || getCommerceHomeUrl();
      const finalUrl = new URL(`${fullUrl}${targetUrl}`);
      if (targetUrl === getCommerceHomeUrl()) {
        finalUrl.searchParams.set('kycSubmitted', 'true');
        if (!hasSeenUseCasesModal) {
          finalUrl.searchParams.set('modal', 'useCases');
        }
      }
      navigateToWithQueryParams(finalUrl.toString());
    } else {
      markSetUpPaymentsComplete().catch(() => {});
      onClose();
    }
  }, [setCurrentEnrollmentFlow, shouldReloadOnEnrollmentComplete, hasSeenUseCasesModal, returnUrl, onClose]);
  useEffect(() => {
    if (businessType) {
      Metrics.counter('stripe-success-modal-viewed', {
        businessType,
        businessStructure: businessStructure || ''
      }).increment();
    }
  }, [businessStructure, businessType]);
  useEffect(() => {
    const alertMessage = currentEnrollmentFlow === EnrollmentFlowType.stripe ? 'applicationSuccessModal.successAlertMessageStripe' : 'applicationSuccessModal.successAlertMessageNative';
    addSuccessAlert({
      title: 'applicationSuccessModal.successAlertTitle',
      message: alertMessage
    });
  }, [currentEnrollmentFlow]);
  const getNextStepsMessage = () => {
    if (sourceApp === 'commerce-account-management-ui') {
      return 'applicationSuccessModal.goToAccountManagement';
    }
    return 'applicationSuccessModal.goToCommerceOverview';
  };
  const renderCta = useCallback(() => {
    const getPrimaryCta = () => {
      if (!shouldReloadOnEnrollmentComplete) {
        return 'applicationSuccessModal.continue';
      }
      if (sourceApp === 'commerce-account-management-ui') {
        return 'applicationSuccessModal.primaryCtaAccountManagement';
      }
      return 'applicationSuccessModal.primaryCtaCommerce';
    };
    const goToPaymentsAccountsButtonClicked = () => {
      if (sourceApp === 'commerce-account-management-ui') {
        return 'clickBackToPaymentsOverviewButton';
      }
      return 'goToCommerceOverviewButtonClicked';
    };
    return /*#__PURE__*/_jsx(TrackedButton, {
      event: events.paymentSettingsInteraction,
      trackingProperties: {
        action: goToPaymentsAccountsButtonClicked(),
        location: 'application-success-modal'
      },
      "data-test-id": TEST_IDS.KYC_SUCCESS_MODAL_VERIFY,
      use: "primary",
      onClick: handleClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getPrimaryCta()
      })
    });
  }, [handleClick, sourceApp, shouldReloadOnEnrollmentComplete]);
  const title = currentEnrollmentFlow === EnrollmentFlowType.stripe ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "applicationSuccessModal.headerStripe"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "applicationSuccessModal.headerNative"
  });
  const mainBodyContent = currentEnrollmentFlow === EnrollmentFlowType.stripe ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "applicationSuccessModal.bodyStripe"
  }) : /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "applicationSuccessModal.bodyNative"
    }), /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "applicationSuccessModal.underwritingLink_jsx",
        elements: {
          Link: KnowledgeBaseButton
        },
        options: {
          onClick: () => {
            track(events.paymentSettingsInteraction, {
              action: 'learnMoreAboutUnderwritingButtonClicked',
              location: 'application-success-modal'
            });
          },
          url: KB_UNDERWRITING,
          external: true
        }
      })
    })]
  });
  return /*#__PURE__*/_jsx(CelebrationModalV2, {
    currentEnrollmentFlow: currentEnrollmentFlow,
    title: title,
    subTitle: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "applicationSuccessModal.subTitle"
    }),
    bodyContent: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(TrackRender, {
        event: events.growthPaymentsPageView,
        properties: {
          screen: 'kyc-approval-success-modal',
          location
        }
      }), mainBodyContent, shouldReloadOnEnrollmentComplete && /*#__PURE__*/_jsx(FormattedMessage, {
        message: getNextStepsMessage()
      })]
    }),
    testID: "kyc-success-modal",
    onClose: () => {
      track(events.paymentSettingsInteraction, {
        action: 'cancelButtonClicked',
        location: 'application-success-modal'
      });
      handleClick();
    },
    primaryCTA: renderCta()
  });
};
export default ApplicationSuccessModal;